import React, { useState, useEffect } from 'react';

import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Dialog from '@mui/material/Dialog';
import Fab from '@mui/material/Fab';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const QuestionMultipleChoice = ({ name, label, value, options = [], answers = [], image, validate, showCorrect, disabled, onChange, ...props }) => {
	const answerValues = answers.map(a => a.value);
	const [isFullscreen, setFullscreen] = useState(false);
	const [internalValue, setInternalValue] = useState({
		selectedAnswers: [],
		isValid: false,
	});

	const isMultiple = answerValues.length > 1;
	const handleChange = (option, isChecked) => {
		const selectedAnswers = (isMultiple && internalValue) ? [...internalValue.selectedAnswers].filter(o => o !== option) : [];
		if (isChecked) selectedAnswers.push(option);
		const isValid = answerValues.every(item => selectedAnswers.includes(item)) && selectedAnswers.every(item => answerValues.includes(item));
		const v = { selectedAnswers, isValid };
		setInternalValue(v);
		onChange({ target: { name, value: v } });
	};

	useEffect(() => {
		setInternalValue(value);
	}, [value]);

	return (
		<FormControl fullWidth sx={{ marginBottom: 4 }} {...props}>
			<FormLabel>{label}</FormLabel>
			{(image?.link || image?.base64) && <img
				style={{ width: '100%', height: '100%', maxHeight: '300px', objectFit: 'scale-down' }}
				src={image?.link || image?.base64 || EmptyImage}
				onClick={() => setFullscreen(true)}
			/>}
			<RadioGroup
				value={value?.value || null}
			>
				{options?.map((o, key) => {
					const isChecked = internalValue?.selectedAnswers?.includes(o.value);
					const isValid = answerValues.includes(o.value);
					const handleClick = (e) => {
						if (disabled || validate) return;
						e.preventDefault(); handleChange(o.value, !isChecked);
					};

					let icon;
					const color = validate
						? (isChecked
							? isValid ? 'green' : 'red'
							: isValid && showCorrect ? 'red' : undefined)
						: undefined;

					if (isMultiple) {
						icon = validate
							? (isChecked
								? isValid ? <CheckBoxIcon /> : <DisabledByDefaultIcon />
								: isValid && showCorrect ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />)
							: (isChecked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />);
					} else {
						icon = validate
							? (isChecked
								? isValid ? <CheckCircleIcon /> : <HighlightOffIcon />
								: isValid && showCorrect ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />)
							: (isChecked ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />);
					}
					return <FormControlLabel
						sx={{
							'.MuiFormControlLabel-label': {
								margin: '5px 0 5px 0',
							},
						}}
						key={key}
						value={o.value}
						control={
							<Radio
								style={{ color }}
								icon={icon}
							/>
						}
						label={o.label || o.value}
						onClick={handleClick}
						style={{ color }}
					/>;
				})}
			</RadioGroup>
			{(image?.link || image?.base64) && <Dialog
				fullScreen
				open={isFullscreen}
				onClose={() => setFullscreen(false)}
			>
				<div style={{ width: '100%', height: '100%', backgroundColor: 'black' }}>
					<img
						style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
						src={image?.link || image?.base64 || EmptyImage}
					/>
				</div>
				<Fab
					color="primary"
					size="small"
					style={{ position: 'absolute', left: 30, top: 30 }}
					onClick={() => setFullscreen(false)}
				>
					<ArrowBackIcon />
				</Fab>
			</Dialog>}
		</FormControl>
	);
};

QuestionMultipleChoice.type = 'QuestionMultipleChoice';
QuestionMultipleChoice.applicability = 'TestSection';

QuestionMultipleChoice.spec = [{
	name: 'QuestionMultipleChoice',
	key: 'QuestionMultipleChoice',
	category: 'input',
	title: 'Multiple Choice Question',
	icon: 'List',
	translatable: ['label', 'options', 'answers'],
	fields: [{
		type: 'TextField',
		label: 'Label',
		name: 'label',
	}, {
		type: 'repeater',
		label: 'Options',
		name: 'options',
		fields: [{
			type: 'TextField',
			name: 'value',
			label: 'Value',
		}],
	}, {
		type: 'AutoComplete',
		label: 'Answers',
		name: 'answers',
		multiple: true,
		useOtherValues: true,
		otherValuesName: 'options',
		fixedBottomPlacement: true,
		popperMaxHeight: '150px',
		getOptionLabel: (o) => o.value,
	},{
		type: 'Upload',
		label: 'Image',
		name: 'image',
		helperText: 'Please upload an image to show next to the question',
		uploadUrl: '/api/files',
		isPublic: true,
	}],
}];

export default QuestionMultipleChoice;
